import { useState, useRef, useEffect, useCallback } from "react";
import {
  ChevronDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import { CheckIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { useClickOutside } from "hooks";
import { createPortal } from "react-dom";
import clsx from "clsx";

export function MultiSelectHierarchicalFilterDropdown({
  filterKey,
  required,
  options = [],
  selectedValues = [],
  onSelect,
  onClear,
  label,
  placeholder = "Select Stage / Sub Stage",
  showAllOption = true,
  showSearch = true,
  searchThreshold = 0,
  className = "",
  usePortal = false,
  portalTarget = null,
  // Controlled state props for COORDINATED closing
  isOpen: propsIsOpen,
  onToggle: propsOnToggle,
  // New props
  toggleChildren = false,
  getDisplayValue,
  error,
}) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Decide whether to use internal or external state
  const isControlled = propsIsOpen !== undefined && propsOnToggle !== undefined;
  const isOpen = isControlled ? propsIsOpen : internalIsOpen;
  const setIsOpen = isControlled ? propsOnToggle : setInternalIsOpen;

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedStages, setExpandedStages] = useState(new Set());
  const dropdownRef = useRef(null);
  const portalRef = useRef(null);
  const buttonRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, (e) => {
    // If click is inside portal, don't close
    if (usePortal && portalRef.current && portalRef.current.contains(e.target)) {
      return;
    }

    if (isOpen) {
      if (isControlled) {
        setIsOpen(null); // Signal parent to close everything
      } else {
        setInternalIsOpen(false);
      }
    }
  });

  const updatePosition = useCallback(() => {
    if (buttonRef.current && isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Open upwards if space below is less than 320px and space above is greater
      const preferUp = spaceBelow < 320 && spaceAbove > spaceBelow;
      const maxH = preferUp 
        ? Math.max(180, spaceAbove - 20) 
        : Math.max(180, spaceBelow - 20);

      const finalWidth = rect.width < 260 ? 260 : rect.width;
      let finalLeft = rect.left;
      if (finalLeft + finalWidth > window.innerWidth - 8) {
        finalLeft = Math.max(8, rect.right - finalWidth);
      }

      setDropdownRect({
        top: rect.bottom,
        rectTop: rect.top,
        left: finalLeft,
        width: rect.width,
        isUp: preferUp,
        maxHeight: maxH,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && usePortal) {
      updatePosition();
      requestAnimationFrame(updatePosition);
      window.addEventListener('resize', updatePosition);

      const handleScroll = (e) => {
        if (portalRef.current && portalRef.current.contains(e.target)) return;
        if (isControlled) {
          setIsOpen(null);
        } else {
          setInternalIsOpen(false);
        }
      };

      window.addEventListener('scroll', handleScroll, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, usePortal, updatePosition, isControlled, setIsOpen]);

  const toggleDropdown = () => {
    if (isControlled) {
      setIsOpen(isOpen ? null : filterKey);
    } else {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  const toggleStageExpansion = (stageId) => {
    setExpandedStages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stageId)) {
        newSet.delete(stageId);
      } else {
        newSet.add(stageId);
      }
      return newSet;
    });
  };

  const filterOptions = (options, searchTerm) => {
    if (!searchTerm) return options;

    return options.map((stage) => {
      const stageMatches = stage.label.toLowerCase().includes(searchTerm.toLowerCase());
      const filteredSubstages = stage.children?.filter((substage) =>
        substage.label.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [];

      // Include stage if it matches or has matching substages
      if (stageMatches || filteredSubstages.length > 0) {
        return {
          ...stage,
          children: stageMatches ? stage.children : filteredSubstages,
        };
      }

      return null;
    }).filter(Boolean);
  };

  const getFilterDisplayValue = (selectedValues, options) => {
    if (!selectedValues || selectedValues.length === 0) return placeholder;

    if (selectedValues.length === 1) {
      const value = selectedValues[0];
      // Search in stages
      for (const stage of options) {
        if (stage.id === value) {
          return stage.label;
        }

        // Search in substages
        if (stage.children) {
          const substage = stage.children.find((sub) => sub.id === value);
          if (substage) {
            return `${stage.label} > ${substage.label}`;
          }
        }
      }
      return value;
    }

    return `${selectedValues.length} selected`;
  };

  const filteredOptions = filterOptions(options, searchTerm);
  const shouldShowSearch =
    showSearch && options && options.length > searchThreshold;

  const handleSelect = (value, type) => {
    const currentValues = Array.isArray(selectedValues) ? [...selectedValues] : [];
    const containsVal = (arr, val) => arr.some(v => String(v) === String(val));

    if (type === "all") {
      // Clear all selections
      onSelect?.(filterKey, [], "all");
      setIsOpen(false);
    } else {
      // Toggle selection
      if (toggleChildren) {
        if (type === "stage") {
          const stage = options.find(opt => opt.id === value);
          const childIds = stage?.children?.map(c => c.id) || [];

          const isCurrentlyChecked = containsVal(currentValues, value) || childIds.some(cid => containsVal(currentValues, cid));

          let newValues;
          if (isCurrentlyChecked) {
            newValues = currentValues.filter(v => String(v) !== String(value) && !childIds.some(cid => String(cid) === String(v)));
          } else {
            newValues = [...currentValues];
            if (!containsVal(newValues, value)) newValues.push(value);
            childIds.forEach(cid => {
              if (!containsVal(newValues, cid)) newValues.push(cid);
            });
          }
          onSelect?.(filterKey, newValues, type);
        } else {
          let newValues;
          if (containsVal(currentValues, value)) {
            newValues = currentValues.filter(v => String(v) !== String(value));
          } else {
            newValues = [...currentValues, value];
          }

          const parent = options.find(opt => opt.children?.some(c => c.id === value));
          if (parent) {
            const parentChildIds = parent.children.map(c => c.id);
            const hasAnyChildSelected = parentChildIds.some(cid => containsVal(newValues, cid));
            if (!hasAnyChildSelected) {
              newValues = newValues.filter(v => String(v) !== String(parent.id));
            } else {
              if (!containsVal(newValues, parent.id)) {
                newValues.push(parent.id);
              }
            }
          }
          onSelect?.(filterKey, newValues, type);
        }
      } else {
        if (containsVal(currentValues, value)) {
          const newValues = currentValues.filter(v => String(v) !== String(value));
          onSelect?.(filterKey, newValues, type);
        } else {
          const newValues = [...currentValues, value];
          onSelect?.(filterKey, newValues, type);
        }
      }
    }

    setSearchTerm("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onClear?.(filterKey);
  };

  const isSelected = (value) => {
    return Array.isArray(selectedValues) && selectedValues.some(v => String(v) === String(value));
  };

  const getStageSelectionState = (option) => {
    if (!option.children || option.children.length === 0) {
      return isSelected(option.id) ? "all" : "none";
    }
    const selectedChildrenCount = option.children.filter(child => isSelected(child.id)).length;
    if (selectedChildrenCount === 0) {
      // If no children selected but parent is selected (e.g. stage without children passed but parent id selected)
      return isSelected(option.id) ? "all" : "none";
    }
    if (selectedChildrenCount === option.children.length) return "all";
    return "partial";
  };

  // True when every top-level option (and, for hierarchical ones, all of its
  // children) is selected — used to highlight the "All" row for a genuine
  // explicit "everything" selection, not just an empty selection.
  const isEverythingSelected =
    options.length > 0 &&
    options.every((option) => getStageSelectionState(option) === "all");

  const renderOption = (option, level = 0) => {
    const isStage = option.type === 'stage';
    const selectionState = isStage ? getStageSelectionState(option) : (isSelected(option.id) ? "all" : "none");
    const isSelectedOption = selectionState !== "none";
    const isExpanded = expandedStages.has(option.id);
    const hasChildren = option.children && option.children.length > 0;

    return (
      <div key={option.id}>
        <button
          className={clsx(
            "flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700",
            isSelectedOption && "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300",
            level === 1 && "pl-8", // Indent substages
          )}
          onClick={() => {
            handleSelect(option.id, option.type);
          }}
        >
          <div className="flex items-center">
            <div
              className={clsx(
                "flex size-4 items-center justify-center rounded border",
                isSelectedOption
                  ? "border-primary-500 bg-primary-500"
                  : "border-gray-300 dark:border-gray-600",
              )}
            >
              {selectionState === "all" && <CheckIcon className="size-3 text-white" />}
              {selectionState === "partial" && <div className="h-0.5 w-2 bg-white rounded-full" />}
            </div>
          </div>
          <div className="ml-2 flex flex-1 items-center justify-between min-w-0">
            <div className="flex items-center flex-1 min-w-0">
              {isStage && hasChildren && (
                <button
                  type="button"
                  className={clsx(
                    "mr-2 flex size-6 items-center justify-center rounded-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors",
                  )}
                  onClick={(e) => {
                    e.stopPropagation(); // Don't trigger selection
                    toggleStageExpansion(option.id);
                  }}
                >
                  <ChevronRightIcon
                    className={clsx(
                      "size-3.5 transition-transform",
                      isExpanded && "rotate-90",
                    )}
                  />
                </button>
              )}
              <span className="flex-1 min-w-0 break-words leading-relaxed">{option.label}</span>
            </div>
          </div>
        </button>

        {/* Render children (substages) if expanded */}
        {isStage && isExpanded && hasChildren && (
          <div className="border-l border-gray-200 dark:border-gray-600">
            {option.children.map((child) => renderOption(child, 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={clsx("relative", className)} ref={dropdownRef}>
      {label && (
        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
          {label}    {required && (
            <span className="text-error">*</span>
          )}
        </label>
      )}

      <div className="relative">
        <Button
          ref={buttonRef}
          variant="outlined"
          className={clsx(
            "flex w-full items-center justify-between gap-2 bg-white px-3 py-2 text-xs transition-colors hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700",
            error
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : (selectedValues && selectedValues.length > 0
                ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                : "border-gray-300 dark:border-gray-600"),
          )}
          onClick={toggleDropdown}
        >
          <span
            className={clsx(
              "truncate",
              selectedValues && selectedValues.length > 0
                ? "text-primary-700 dark:text-primary-300 font-medium"
                : "text-gray-500 dark:text-gray-400",
            )}
          >
            {getDisplayValue ? getDisplayValue(selectedValues, options) : getFilterDisplayValue(selectedValues, options)}
          </span>
          <div className="flex items-center gap-1">
            {selectedValues && selectedValues.length > 0 && (
              <XCircleIcon
                className="size-4 text-gray-400 transition-colors hover:text-red-500 cursor-pointer"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={handleClear}
              />
            )}
            <ChevronDownIcon
              className={clsx(
                "size-4 transition-transform",
                isOpen && "rotate-180",
              )}
            />
          </div>
        </Button>

        {isOpen && (() => {
          const dropdownContent = (
            <div
              ref={portalRef}
              className={clsx(
                "z-[200000] mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800",
                usePortal ? "fixed" : "absolute w-full"
              )}
              style={usePortal && dropdownRect ? {
                top: dropdownRect.isUp ? undefined : `${dropdownRect.top + 4}px`,
                bottom: dropdownRect.isUp ? `${window.innerHeight - dropdownRect.rectTop + 4}px` : undefined,
                left: `${dropdownRect.left}px`,
                width: `${dropdownRect.width}px`,
                maxWidth: '400px'
              } : {}}
            >
              {/* Search */}
              {shouldShowSearch && (
                <div className="border-b border-gray-200 p-3 dark:border-gray-600">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
                    <input
                      type="text"
                      placeholder={`Search ${label?.toLowerCase() || "stages"}...`}
                      className="focus:ring-primary-500 w-full rounded-md border placeholder-gray-500 dark:placeholder-gray-400 dark:focus:border-primary-500 border-gray-300 bg-white py-2 pr-3 pl-10 text-sm text-gray-900 focus:ring-1 focus:outline-none focus:border-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Options */}
              <div 
                className="overflow-y-auto custom-scrollbar"
                style={{
                  maxHeight: usePortal && dropdownRect?.maxHeight 
                    ? `${dropdownRect.maxHeight - 100}px` 
                    : '240px'
                }}
              >
                {/* All option */}
                {showAllOption && (() => {
                  const isAllChecked =
                    !selectedValues ||
                    selectedValues.length === 0 ||
                    isEverythingSelected;
                  return (
                    <button
                      className={clsx(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700",
                        isAllChecked &&
                        "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300",
                      )}
                      onClick={() => handleSelect("all", "all")}
                    >
                      <div className="flex items-center">
                        <div
                          className={clsx(
                            "flex size-4 items-center justify-center rounded border",
                            isAllChecked
                              ? "border-primary-500 bg-primary-500"
                              : "border-gray-300 dark:border-gray-600"
                          )}
                        >
                          {isAllChecked && (
                            <CheckIcon className="size-3 text-white" />
                          )}
                        </div>
                      </div>
                      <span className="ml-2">All {label}</span>
                    </button>
                  );
                })()}

                {/* Hierarchical options */}
                {filteredOptions.map((option) => renderOption(option))}

                {filteredOptions.length === 0 && searchTerm && (
                  <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No options found for `{searchTerm}`
                  </div>
                )}

                {filteredOptions.length === 0 && !searchTerm && (
                  <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No records found
                  </div>
                )}
              </div>
            </div>
          );

          if (usePortal && typeof document !== 'undefined') {
            return createPortal(dropdownContent, portalTarget || document.body);
          }

          return dropdownContent;
        })()}
      </div>
      {error && (
        <span className="mt-1 block text-xs text-red-500 font-medium">{error}</span>
      )}
    </div>
  );
}
